// API base URL - update this to match your backend server
const API_BASE_URL = 'http://localhost:8000/api/v1';

// Helper function to get auth token from localStorage
const getAuthToken = () => {
  return localStorage.getItem('authToken') || localStorage.getItem('token');
};

class SolarPanelApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async calculateCarbonCredits(applicationId) {
    const response = await fetch(`${this.baseURL}/solar-panel/applications/${applicationId}/calculate-carbon-credits`, {
      method: 'POST',
    });
    return this.handleResponse(response);
  }

  // Helper method to handle API responses
  async handleResponse(response) {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || errorData.message || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  // Create a new solar panel application
  async createApplication(formData) {
    console.log('API Service: Creating application with data:', formData);
    const formDataToSend = new FormData();
    
    // Add text fields
    formDataToSend.append('full_name', formData.fullName);
    if (formData.companyName) {
      formDataToSend.append('company_name', formData.companyName);
    }
    formDataToSend.append('aadhar_card', formData.aadharCard);
    
    // Ensure URL has proper protocol
    let apiLink = formData.apiLink;
    if (apiLink && !apiLink.startsWith('http://') && !apiLink.startsWith('https://')) {
      apiLink = 'https://' + apiLink;
    }
    formDataToSend.append('api_link', apiLink);

    // Add files
    if (formData.ownershipDocument) {
      formDataToSend.append('ownership_document', formData.ownershipDocument);
    }
    if (formData.energyCertification) {
      formDataToSend.append('energy_certification', formData.energyCertification);
    }
    if (formData.geotagPhoto) {
      formDataToSend.append('geotag_photo', formData.geotagPhoto);
    }

    console.log('API Service: Making request to:', `${this.baseURL}/solar-panel/applications`);
    
    const response = await fetch(`${this.baseURL}/solar-panel/applications`, {
      method: 'POST',
      body: formDataToSend,
    });

    console.log('API Service: Response status:', response.status);
    return this.handleResponse(response);
  }

  // Get user applications
  async getUserApplications(skip = 0, limit = 100) {
    const response = await fetch(
      `${this.baseURL}/solar-panel/applications?skip=${skip}&limit=${limit}`
    );
    return this.handleResponse(response);
  }

  // Get specific application
  async getApplication(applicationId) {
    const response = await fetch(`${this.baseURL}/solar-panel/applications/${applicationId}`);
    return this.handleResponse(response);
  }

  // Update application
  async updateApplication(applicationId, updateData) {
    const response = await fetch(`${this.baseURL}/solar-panel/applications/${applicationId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updateData),
    });
    return this.handleResponse(response);
  }

  // Delete application
  async deleteApplication(applicationId) {
    const response = await fetch(`${this.baseURL}/solar-panel/applications/${applicationId}`, {
      method: 'DELETE',
    });
    return this.handleResponse(response);
  }

  // Calculate solar energy potential using your backend
  async calculateSolarEnergy(latitude, longitude, panelAreaSqm = null) {
    console.log('API Service: Calculating solar energy for:', latitude, longitude);
    
    const formData = new FormData();
    formData.append('latitude', latitude);
    formData.append('longitude', longitude);
    if (panelAreaSqm) {
      formData.append('panel_area_sqm', panelAreaSqm);
    }

    const response = await fetch(`${this.baseURL}/solar-panel/calculate-solar-energy`, {
      method: 'POST',
      body: formData,
    });

    const result = await this.handleResponse(response);
    console.log('API Service: Solar calculation result:', result);
    return result;
  }

  // Extract GPS coordinates from photo using OpenAI Vision API
  async extractGpsFromPhoto(photo) {
    console.log('API Service: Extracting GPS from photo:', photo.name);
    
    const formData = new FormData();
    formData.append('photo', photo);

    const response = await fetch(`${this.baseURL}/solar-panel/extract-gps`, {
      method: 'POST',
      body: formData,
    });

    const result = await this.handleResponse(response);
    console.log('API Service: GPS extraction result:', result);
    
    // Convert to the format expected by your React component
    return {
      is_valid: result.success,
      latitude: result.latitude,
      longitude: result.longitude,
      message: result.message,
      method: result.method,
      confidence: result.confidence,
      description: result.description
    };
  }

  // Upload document
  async uploadDocument(file, fileType) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    const response = await fetch(`${this.baseURL}/solar-panel/upload-document`, {
      method: 'POST',
      body: formData,
    });

    return this.handleResponse(response);
  }

  /**
   * UNIFIED CARBON COIN MINTING METHOD
   * Mint carbon coins directly using the /solar-panel/mint-coin endpoint
   * @param {Object|string} coinDataOrName - Either an object with coin data or just the name (for backward compatibility)
   * @param {number} credits - Number of carbon credits/coins (if first param is string)
   * @param {string} source - Source type (if first param is string)
   * @param {string} description - Optional description (if first param is string)
   * @returns {Promise<Object>} Minting result
   */
  async mintCarbonCoins(coinDataOrName, credits = null, source = 'solar_plant', description = null) {
    try {
      let coinData;
      
      // Handle both old and new calling patterns
      if (typeof coinDataOrName === 'object') {
        // New object-based calling pattern
        coinData = coinDataOrName;
      } else {
        // Old parameter-based calling pattern (for backward compatibility)
        coinData = {
          name: coinDataOrName,
          credits: credits,
          source: source,
          description: description
        };
      }

      console.log('API Service: Minting carbon coins with data:', coinData);
      
      const formData = new FormData();
      formData.append('name', coinData.name);
      formData.append('credits', coinData.credits.toString());
      formData.append('source', coinData.source || 'solar_plant');
      if (coinData.description) {
        formData.append('description', coinData.description);
      }

      const response = await fetch(`${this.baseURL}/solar-panel/mint-coin`, {
        method: 'POST',
        body: formData,
      });

      const result = await this.handleResponse(response);
      console.log('API Service: Minting result:', result);
      return result;
    } catch (error) {
      console.error('API Service: Minting error:', error);
      throw error;
    }
  }

  /**
   * Mint carbon coins directly using the new mint-coin endpoint
   * @param {Object} coinData - The coin minting data
   * @param {string} coinData.name - Name of the coin/project
   * @param {number} coinData.credits - Number of carbon credits/coins
   * @param {string} coinData.source - Source type (default: "solar_plant")
   * @param {string} coinData.description - Optional description
   * @returns {Promise<Object>} Minting result
   */
  async mintCarbonCoinsDirect(coinData) {
    try {
      const formData = new FormData();
      formData.append('name', coinData.name);
      formData.append('credits', coinData.credits.toString());
      formData.append('source', coinData.source || 'solar_plant');
      if (coinData.description) {
        formData.append('description', coinData.description);
      }

      const response = await fetch(`${this.baseURL}/solar-panel/mint-coin`, {
        method: 'POST',
        body: formData,
        headers: {
          // Don't set Content-Type for FormData, let browser set it
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Direct minting result:', result);
      return result;
    } catch (error) {
      console.error('Direct minting error:', error);
      throw error;
    }
  }

  /**
   * Get minted coin information using the GET mint-coin endpoint
   * @param {Object} options - Query options
   * @param {string} options.name - Filter by coin name
   * @param {string} options.source - Filter by source type
   * @param {number} options.skip - Pagination offset
   * @param {number} options.limit - Number of items per page
   * @returns {Promise<Object>} Minted coins information
   */
  async getMintCoinInfo(options = {}) {
    try {
      const params = new URLSearchParams();
      
      if (options.name) params.append('name', options.name);
      if (options.source) params.append('source', options.source);
      if (options.skip !== undefined) params.append('skip', options.skip.toString());
      if (options.limit !== undefined) params.append('limit', options.limit.toString());

      const url = `${this.baseURL}/solar-panel/mint-coin?${params}`;
      console.log('Fetching mint coin info from:', url);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Mint coin info result:', result);
      return result;
    } catch (error) {
      console.error('Get mint coin info error:', error);
      throw error;
    }
  }

  // Get all minted coins
  async getMintedCoins(skip = 0, limit = 100) {
    const response = await fetch(`${this.baseURL}/solar-panel/minted-coins?skip=${skip}&limit=${limit}`);
    return this.handleResponse(response);
  }

  // Get specific minted coin by ID
  async getMintedCoin(coinId) {
    const response = await fetch(`${this.baseURL}/solar-panel/minted-coins/${coinId}`);
    return this.handleResponse(response);
  }

  // Get application stats
  async getApplicationStats() {
    const response = await fetch(`${this.baseURL}/solar-panel/stats`);
    return this.handleResponse(response);
  }

  // Admin endpoints
  async getAllApplicationsAdmin(skip = 0, limit = 100, status = null) {
    let url = `${this.baseURL}/solar-panel/admin/applications?skip=${skip}&limit=${limit}`;
    if (status) {
      url += `&status=${status}`;
    }
    
    const response = await fetch(url);
    return this.handleResponse(response);
  }

  async updateApplicationStatus(applicationId, status, verificationNotes = null) {
    const formData = new FormData();
    formData.append('status', status);
    if (verificationNotes) {
      formData.append('verification_notes', verificationNotes);
    }

    const response = await fetch(`${this.baseURL}/solar-panel/admin/applications/${applicationId}/status`, {
      method: 'PUT',
      body: formData,
    });

    return this.handleResponse(response);
  }

  /**
   * Mint carbon coins for a solar panel application using the new central system
   * @param {Object} mintingData - Solar calculation data for minting
   * @returns {Promise} - Minting result with issue_id and coin details
   */
  async mintCarbonCoinsToSystem(mintingData) {
    try {
      const formData = new FormData();
      formData.append('latitude', mintingData.latitude);
      formData.append('longitude', mintingData.longitude);
      formData.append('annual_energy_mwh', mintingData.annual_energy_mwh);
      formData.append('annual_co2_avoided_tonnes', mintingData.annual_co2_avoided_tonnes);
      formData.append('annual_carbon_credits', mintingData.annual_carbon_credits);
      formData.append('calculation_method', mintingData.calculation_method);
      formData.append('issuer_name', mintingData.issuer_name || 'Solar Panel Owner');
      formData.append('description', mintingData.description || 'Solar panel carbon credits');
      formData.append('source_project_id', mintingData.source_project_id);

      const response = await fetch(`${this.baseURL}/solar-panel/mint-carbon-coins`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to mint carbon coins');
      }

      return await response.json();
    } catch (error) {
      console.error('Error minting carbon coins:', error);
      throw error;
    }
  }

  /**
   * Get user's carbon coin history
   * @param {number} skip - Pagination offset
   * @param {number} limit - Number of items per page
   * @param {string} source - Filter by source ('solar_panel' or 'forestation')
   * @returns {Promise} - List of carbon coin issues
   */
  async getCarbonCoinHistory(skip = 0, limit = 100, source = null) {
    try {
      const params = new URLSearchParams({
        skip: skip.toString(),
        limit: limit.toString()
      });
      
      if (source) {
        params.append('source', source);
      }

      const response = await fetch(`${this.baseURL}/carbon-coins/?${params}`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch carbon coin history');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching carbon coin history:', error);
      throw error;
    }
  }

  /**
   * Get carbon coin statistics for the user
   * @returns {Promise} - User's carbon coin stats
   */
  async getCarbonCoinStats() {
    try {
      const response = await fetch(`${this.baseURL}/carbon-coins/stats`, {
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch carbon coin stats');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching carbon coin stats:', error);
      throw error;
    }
  }

  // Save analysis results to database
  async saveSolarAnalysisResults(analysisData) {
    console.log('API Service: Saving analysis results:', analysisData);
    
    const response = await fetch(`${this.baseURL}/solar-panel/analysis`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(analysisData),
    });

    return this.handleResponse(response);
  }

  // Create carbon token
  async createCarbonToken(tokenData) {
    console.log('API Service: Creating carbon token:', tokenData);
    
    const response = await fetch(`${this.baseURL}/solar-panel/tokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(tokenData),
    });

    return this.handleResponse(response);
  }
}

// Create and export a singleton instance
const solarPanelApiService = new SolarPanelApiService();
export default solarPanelApiService;